import { positionableUtil } from '@stormwater-software/common';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Icon } from 'ol/style';
import {
  ASSET_MARKER_SCALE,
  buildAssetFeatureableData,
  renderAssetMarkerDataUrl,
  symbolCodeLabel,
} from './assets';

/**
 * Positionable util for asset markers on the site map.
 *
 * The base `PositionableUtil` class is not exported from `@stormwater-software/common`, so we
 * derive it from an existing util instance's prototype chain and extend it. This
 * inherits the point draw/feature behaviour and overrides only what assets need:
 * the marker is the asset type's pre-rendered featureable icon.
 */
const PositionableUtil = Object.getPrototypeOf(
  Object.getPrototypeOf(positionableUtil.commentables),
).constructor;

// drawn 1:1 on screen rather than resized — resizing is what blurred them.
const ICON_SCALE = ASSET_MARKER_SCALE;

// Cache the rendered symbols so getIcon/getLayer don't re-rasterise on every call.
const symbolCache = {};

/**
 * The asset type's plain badge — used by lists (the map editor sidebar and map
 * key), where the per-asset status decorations would only add noise.
 */
const typeIconSrc = (sourceModel) => {
  const featureable = sourceModel?.asset_type?.featureable_data;
  const config = featureable?.config;

  if (!config || !(config.color || config.code)) {
    return featureable?.dataurl || null;
  }

  const color = config.color || '#9CA3AF';
  const key = `type|${color}|${config.code}`;

  if (!symbolCache[key]) {
    symbolCache[key] = buildAssetFeatureableData(color, config.code).dataurl;
  }

  return symbolCache[key] || featureable.dataurl || null;
};

/**
 * The map marker, rendered per asset: the type-coloured badge labelled
 * `CODE-ID`, plus the inspection check status and a chip per tied finding.
 */
const markerSrc = (sourceModel) => {
  const featureable = sourceModel?.asset_type?.featureable_data;
  const config = featureable?.config;

  if (!config || !(config.color || config.code)) {
    return featureable?.dataurl || null;
  }

  const color = config.color || '#9CA3AF';
  const label = [symbolCodeLabel(config.code), sourceModel.asset_name]
    .filter(Boolean)
    .join('-');

  // Check status: every required inspectable field answered for this inspection.
  const checks = (sourceModel.asset_type?.fields || []).filter(
    (f) => f.is_inspectable && f.is_active,
  );
  const checksComplete = checks.length
    ? checks
        .filter((f) => f.is_required)
        .every((f) =>
          (sourceModel.inspection_answers || []).some(
            (a) => a.asset_field_type_id === f.id && (a.value || '').trim(),
          ),
        )
    : undefined;
  const findings = (sourceModel.findings || [])
    .filter((f) => f.type)
    .map((f) => ({ type: f.type, color: f.finding_type?.details?.[0]?.color }));

  const key = `marker|${color}|${label}|${checksComplete}|${findings
    .map((f) => `${f.type}${f.color}`)
    .join(',')}`;

  if (!symbolCache[key]) {
    symbolCache[key] = renderAssetMarkerDataUrl({
      color,
      label,
      checksComplete,
      findings,
    });
  }

  return symbolCache[key] || featureable.dataurl || null;
};

export class AssetTypePositionableUtil extends PositionableUtil {
  getName = (sourceModel) =>
    sourceModel.asset_name || `Asset #${sourceModel.id}`;

  getIcon = (sourceModel) => {
    const src = typeIconSrc(sourceModel);

    return src ? { src } : null;
  };

  getLayer = (sourceModel) => {
    const layer = new VectorLayer({ source: new VectorSource({ wrapX: false }) });
    const src = markerSrc(sourceModel);

    if (src) {
      layer.setStyle(
        new Style({ image: new Icon({ src, scale: ICON_SCALE }) }),
      );
    }

    return layer;
  };

  /** Stored as the positionable_config for placed asset markers. */
  getInstanceConfig = () => ({ radius: 45 });

  /** Assets are edited from the inspection's Assets tab, not the map. */
  canEditSourceModel = () => false;

  /** One marker per asset per map, so draw mode ends after the first placement. */
  placeOncePerTarget = () => true;

  /**
   * No hover overlay: clicking an asset marker opens the asset card
   * (AssetMapCard) instead, which carries the detail and its actions.
   */
  hasOverlay = () => false;
}

const assetTypeUtil = new AssetTypePositionableUtil();

export default assetTypeUtil;
